import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { useSheetFocus } from '@/lib/use-sheet-focus'

import type { SidebarNavItem } from '../app-shell/navigation-types'
import type { AppShellUser } from '../app-shell/user-display'
import { UserMobileDrawer } from '@/features/user/shell/mobile-drawer'
import { MobileDrawerContext } from './mobile-drawer-context'

/**
 * 移动端用户壳层:抽屉 + 全高内容区。
 * 不再渲染统一的「工作区」标题栏——header 由各页面按设计稿自渲染,
 * 页面通过 MobileDrawerContext 打开抽屉。
 */
export function UserMobileShell({
  activePath,
  children,
  navigation = [],
  user,
}: {
  activePath?: string
  children: ReactNode
  navigation?: SidebarNavItem[]
  user: AppShellUser
}) {
  const { t } = useTranslation('shell')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const asideRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    document.documentElement.dataset.mobileShellActive = 'true'
    return () => {
      delete document.documentElement.dataset.mobileShellActive
    }
  }, [])

  const closeDrawer = () => {
    setDrawerOpen(false)
  }

  // 抽屉与底部 sheet 同一套焦点基线:打开迁移焦点、Tab 围栏、Escape 关闭、关闭恢复触发按钮。
  useSheetFocus(asideRef, drawerOpen, closeDrawer)
  const drawerContext = useMemo(
    () => ({
      closeDrawer,
      drawerOpen,
      openDrawer: () => {
        setDrawerOpen(true)
      },
    }),
    [drawerOpen],
  )

  return (
    <MobileDrawerContext.Provider value={drawerContext}>
      <div
        className="fixed inset-0 overflow-hidden bg-[var(--color-sidebar-bg)] text-[var(--color-text)]"
        data-mobile-shell
        data-navigation-open={drawerOpen}
      >
        <aside
          ref={asideRef}
          aria-hidden={!drawerOpen}
          className={cn(
            'fixed inset-y-0 left-0 z-10 flex w-[86vw] flex-col overflow-hidden bg-[var(--color-sidebar-bg)] transition-transform duration-[280ms] [transition-timing-function:cubic-bezier(.22,.7,.3,1)]',
            drawerOpen ? 'translate-x-0' : '-translate-x-full',
          )}
          inert={!drawerOpen ? true : undefined}
        >
          <UserMobileDrawer
            activePath={activePath}
            navigation={navigation}
            onNavigate={() => {
              setDrawerOpen(false)
            }}
            user={user}
          />
        </aside>

        <main
          className={cn(
            // box-shadow 不参与过渡:否则展开时阴影从 0 模糊半径"生长",与滑动不同步。
            'relative z-20 h-full min-w-0 overflow-clip bg-white transition-[translate,transform,border-radius] duration-[280ms] [transition-timing-function:cubic-bezier(.22,.7,.3,1)]',
            drawerOpen
              ? 'translate-x-[86vw] rounded-2xl shadow-[-5px_0_15px_rgba(26,28,25,0.09)]'
              : 'rounded-none shadow-none',
          )}
          inert={drawerOpen ? true : undefined}
        >
          {children}
        </main>

        {drawerOpen ? (
          <button
            aria-label={t('mobile.closeNavigation')}
            // 设计稿右侧页面不变暗:遮罩只承担点击关闭,透明。
            // 左侧圆角与 main 的 rounded-2xl 对齐,避免盖住圆角缺口。
            className="fixed inset-y-0 left-[86vw] right-0 z-30 rounded-l-2xl"
            onClick={() => {
              setDrawerOpen(false)
            }}
            type="button"
          />
        ) : null}
      </div>
    </MobileDrawerContext.Provider>
  )
}
